import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Plus,
  Users,
  Calendar,
  Package,
  ChevronRight,
  Phone,
  FileCheck,
  Ticket
} from 'lucide-react'
import { AgentAPI, Allocation, Passenger } from '../services/api'
import BottomNav from '../components/BottomNav'

const AllocationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [allocation, setAllocation] = useState<Allocation | null>(null)
  const [passengers, setPassengers] = useState<Passenger[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return
      
      try {
        setLoading(true)
        const [allocData, passData] = await Promise.all([
          AgentAPI.getAllocation(parseInt(id)),
          AgentAPI.getMyPassengers(parseInt(id))
        ])
        setAllocation(allocData)
        setPassengers(passData)
      } catch (err: any) {
        setError(err.message || 'Failed to load allocation')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [id])

  // Get status colors
  const getBookingStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800'
      case 'tentative': return 'bg-amber-100 text-amber-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getVisaStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600'
      case 'applied': return 'text-blue-600'
      case 'documents_collected': return 'text-amber-600'
      case 'rejected': return 'text-red-600'
      default: return 'text-gray-500'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error || !allocation) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <p className="text-red-600 mb-4">{error || 'Allocation not found'}</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-primary-500 text-white rounded-xl"
        >
          Go Back
        </button>
      </div>
    )
  }

  const availableSlots = allocation.available_qty

  return (
    <div className="min-h-screen bg-gray-100 pb-safe">
      {/* Header */}
      <div className="bg-primary-500 text-white p-4 pb-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-primary-100 mb-4"
        >
          <ArrowLeft size={20} />
          Back
        </button>
        <h1 className="text-xl font-bold">{allocation.series_name || `Series #${allocation.series_id}`}</h1>
        <p className="text-primary-200">{allocation.series_code || 'No code'}</p>
      </div>

      <div className="p-4 -mt-3 space-y-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-3 text-center">
            <p className="text-xs text-gray-500">Allocated</p>
            <p className="text-lg font-bold text-gray-900">{allocation.allocated_qty}</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center">
            <p className="text-xs text-gray-500">Sold</p>
            <p className="text-lg font-bold text-green-600">{allocation.sold_qty}</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center">
            <p className="text-xs text-gray-500">Available</p>
            <p className="text-lg font-bold text-blue-600">{allocation.available_qty}</p>
          </div>
        </div>

        {/* Travel Info */}
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center gap-3 text-gray-600">
            <Calendar size={18} />
            <div>
              <p className="text-sm">Departure</p>
              <p className="font-medium text-gray-900">
                {allocation.departure_date 
                  ? new Date(allocation.departure_date).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                    })
                  : 'TBD'}
              </p>
            </div>
          </div>
          {allocation.cutoff_date && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 text-amber-600">
              <Package size={18} />
              <div>
                <p className="text-sm">Cutoff Date</p>
                <p className="font-medium">
                  {new Date(allocation.cutoff_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Add Passenger Button */}
        {availableSlots > 0 && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(`/allocation/${id}/add-passenger`)}
            className="w-full flex items-center justify-center gap-2 py-3 bg-green-500 text-white rounded-xl font-medium"
          >
            <Plus size={20} />
            Add Passenger ({availableSlots} slots left)
          </motion.button>
        )}

        {/* Passengers List */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Passengers ({passengers.length})</h3>
          </div>

          {passengers.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">No passengers added yet</p>
              {availableSlots > 0 && (
                <button
                  onClick={() => navigate(`/allocation/${id}/add-passenger`)}
                  className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg"
                >
                  Add First Passenger
                </button>
              )}
            </div>
          ) : (
            passengers.map((passenger, index) => (
              <motion.div
                key={passenger.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{passenger.full_name}</h4>
                    <p className="text-sm text-gray-500 capitalize">{passenger.room_type} sharing</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBookingStatusColor(passenger.booking_status)}`}>
                    {passenger.booking_status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 text-sm mt-3">
                  {passenger.mobile_number && (
                    <a
                      href={`tel:${passenger.mobile_number}`}
                      className="flex items-center gap-1 text-primary-600"
                    >
                      <Phone size={14} />
                      {passenger.mobile_number}
                    </a>
                  )}
                  <div className={`flex items-center gap-1 ${getVisaStatusColor(passenger.visa_status)}`}>
                    <FileCheck size={14} />
                    <span className="capitalize">{passenger.visa_status.replace('_', ' ')}</span>
                  </div>
                  <div className={`flex items-center gap-1 ${passenger.ticket_status === 'issued' ? 'text-green-600' : 'text-gray-500'}`}>
                    <Ticket size={14} />
                    <span className="capitalize">{passenger.ticket_status}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                  <div className="text-sm">
                    <span className="text-gray-500">Paid: </span>
                    <span className={passenger.payment_status === 'full' ? 'text-green-600' : 'text-amber-600'}>
                      PKR {passenger.amount_paid.toLocaleString()} / {passenger.sale_price.toLocaleString()}
                    </span>
                  </div>
                  <ChevronRight size={18} className="text-gray-400" />
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      <BottomNav activeTab="home" />
    </div>
  )
}

export default AllocationDetail
